import { List, useTable } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import { useNavigate, useSearchParams } from 'react-router';
import { Link } from 'react-router';
import type { Company } from '@viq-admin/types';
import { archiveCompany } from '../../api/companies';
import { CompanyFeatureTags } from './hub/CompanyFeatureTags';
import {
  DataTable,
  RowActionsMenu,
  useUrlTableState,
  type DataColumn,
  type TableState,
} from '@viq-admin/ui';

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — an inline literal here would bust
 * DataTable's `effective` memo (and, through it, `prepared`/`finalColumns`)
 * on every render for no reason. See DataTable's `defaultState` JSDoc.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'name', order: 'ascend' },
};

/**
 * All-tenant list of active companies.
 *
 * A Features column shows each tenant's switched-on flags as tags (the same
 * tags as the company page header), so "who has HubSpot / card images" reads
 * off the list without opening each company.
 *
 * Read access gates the page (via the route's RequireCan); `manage:Company`
 * gates the per-row Archive action. Opening a company is the ROW CLICK — there
 * is deliberately no Edit or View button (see the DataTable spec).
 */
export function CompanyList() {
  const { tableProps } = useTable<Company>({
    resource: 'companies',
    // DataTable owns paging; Refine must not slice the payload.
    pagination: { mode: 'off' },
  });
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);

  const columns: DataColumn<Company>[] = [
    { dataIndex: 'name', key: 'name', title: 'Name', kind: 'text', width: 260 },
    { dataIndex: 'entraHint', key: 'entraHint', title: 'Entra Hint', kind: 'text', width: 220 },
    {
      // The switched-on flags at a glance. Display only: not sortable,
      // filterable or searched.
      key: 'features',
      title: 'Features',
      kind: 'text',
      width: 280,
      sortable: false,
      filterable: false,
      render: (_: unknown, record: Company) => <CompanyFeatureTags company={record} />,
    },
    { dataIndex: 'createdAt', key: 'createdAt', title: 'Created', kind: 'date' },
    { dataIndex: 'updatedAt', key: 'updatedAt', title: 'Updated', kind: 'date' },
    {
      key: 'actions',
      title: '',
      kind: 'actions',
      render: (_: unknown, record: Company) => (
        <RowActionsMenu
          actions={[
            {
              key: 'archive',
              label: 'Archive',
              danger: true,
              cap: 'manage:Company',
              onRun: async () => {
                await archiveCompany(record.id);
                invalidate({ resource: 'companies', invalidates: ['list'] });
              },
              confirm: {
                title: `Archive ${record.name}?`,
                content:
                  'This company will no longer be able to access the platform. You can restore it from the archived companies page.',
                okText: 'Archive',
                danger: true,
                successMessage: `Archived ${record.name}.`,
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <Link to="/companies/archived">View archived →</Link>
        </>
      )}
    >
      <DataTable<Company>
        tableProps={tableProps}
        columns={columns}
        rowKey="id"
        storageKey="companiesList"
        state={state}
        onStateChange={setState}
        defaultState={DEFAULT_STATE}
        searchFields={['name', 'entraHint']}
        searchPlaceholder="Search companies"
        onRowClick={(record) => navigate(`/companies/${record.id}`)}
        empty={{
          title: 'No companies yet',
          description: 'Create the first tenant to get started.',
        }}
      />
    </List>
  );
}
